import { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { cachedGet } from "../lib/sharedData";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import {
  Mail, MessageCircle, MessageSquare, Phone, Search, Send, Lock, Inbox as InboxIcon,
  Globe, Tag, AlertTriangle, Building2, User as UserIcon, Scale, HeartHandshake,
  Eye, ChevronDown, Sparkles, Zap, Paperclip, RefreshCw,
  FileText, FileImage, Download, NotebookPen, FolderInput, Check, X, Loader2, MessageSquarePlus, ChevronLeft, MoreHorizontal,
} from "lucide-react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import ReportPickerModal from "../components/ReportPickerModal";
import { useT, locale } from "../lib/i18n";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle, call: Phone, outlook: Mail, gmail: Mail };
const CHANNEL_LABEL = { email: "Email", sms: "SMS", whatsapp: "WhatsApp", call: "Call", outlook: "Outlook", gmail: "Gmail" };

const CATEGORY_META = {
  club:           { label: "Club",           tone: "bg-sky-100 text-sky-800",        icon: Building2 },
  client:         { label: "Client",         tone: "bg-emerald-100 text-emerald-800", icon: UserIcon },
  family:         { label: "Family",         tone: "bg-violet-100 text-violet-800",  icon: HeartHandshake },
  legal:          { label: "Legal",          tone: "bg-amber-100 text-amber-800",    icon: Scale },
  governing_body: { label: "Governing body", tone: "bg-rose-100 text-rose-800",      icon: Globe },
  scout:          { label: "Scout",          tone: "bg-slate-200 text-slate-700",    icon: Eye },
  media:          { label: "Media",          tone: "bg-slate-200 text-slate-700",    icon: Tag },
  unknown:        { label: "Unknown",        tone: "bg-slate-100 text-slate-600",    icon: Tag },
};

const CATEGORIES = ["club", "client", "family", "legal", "governing_body", "scout", "media", "unknown"];
const CHANNELS = ["email", "sms", "whatsapp", "call"];

// Strip CSS-only artifacts left behind by buggy IMAP parsers — e.g. emails
// whose <style> block was de-tagged but whose CSS rules survived as plain
// text (visible as a hundred-line `@font-face { ... } body { ... }` dump).
// Also drops SendGrid template placeholders like `%%subject%%`.
function stripCssArtifacts(s) {
  if (!s || typeof s !== "string") return s;
  let out = s
    .replace(/%%[a-z_]+%%/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/-->/g, "")                       // dangling HTML comment tails
    .replace(/\/\*[\s\S]*?\*\//g, "")          // CSS comments
    .replace(/@font-face\s*\{[^}]*\}/gi, "")
    .replace(/@media[^{]*\{(?:[^{}]|\{[^}]*\})*\}/gi, "")
    .replace(/@import\s+[^;]+;/gi, "")
    .replace(/@charset\s+[^;]+;/gi, "")
    // class/id selectors with full rule bodies, incl. comma-chains
    .replace(/(?:^|[\s>+~])[#.][\w-]+(?:\s*[,>+~]\s*[#.\w-]+)*\s*\{[^}]*\}/g, " ")
    // bare tag selectors with rule bodies (body { ... }, td { ... } etc.)
    .replace(/\b(?:body|html|table|td|th|tr|div|span|a|p|h[1-6]|img|ul|ol|li|hr|font|center|caption|article|section|nav|footer|header)\s*\{[^}]*\}/gi, " ")
    // pseudo-classes left behind ( a:hover { ... } )
    .replace(/[a-z][\w-]*\s*:[\w-]+\s*\{[^}]*\}/gi, " ")
    // Dangling selector lists left after rule bodies were stripped (e.g.
    // "#outlook a, .ExternalClass p, .ExternalClass span" with no { ... }).
    .replace(/(?:^|[\s>])(?:[#.][\w-]+|\*)(?:\s+[\w-]+)?(?:\s*,\s*(?:[#.][\w-]+|\*)(?:\s+[\w-]+)?){1,}/g, " ");
  // Strip orphan CSS selectors (#id, .class, "#id tag") that survived at the
  // very start of the cleaned text after the brace-stripping passes.
  out = out.replace(/^(?:\s*(?:[#.][\w-]+|\*)(?:\s+[\w-]+)?\s*)+/, "");
  // Collapse the whitespace left behind so the remaining text reads cleanly.
  out = out.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  return out;
}


function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 3)
    .join("")
    .toUpperCase();
}

function relativeTime(iso) {
  if (!iso) return "—";
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now - d;
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function useIsMobile() {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const fn = (e) => setM(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
}

export default function UnifiedInbox() {
  const t = useT();
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ channel: null, category: null, unread: false });
  const [q, setQ] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [composerKind, setComposerKind] = useState("reply"); // reply | note
  const [showReportPicker, setShowReportPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [aiInsight, setAiInsight] = useState(null); // { urgency, case_type, summary, sentiment, suggested_actions, stub }
  const [aiInsightLoading, setAiInsightLoading] = useState(false);
  const [visibility, setVisibility] = useState("hybrid"); // workspace inbox visibility mode
  const [scope, setScope] = useState(() => localStorage.getItem("pm_inbox_scope") || "all"); // all | mine
  const threadRef = useRef(null);
  const loadMoreObs = useRef(null);

  const PAGE = 30;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = { limit: PAGE, offset: messages.length };
      if (filters.channel) params.channel = filters.channel;
      if (filters.category) params.category = filters.category;
      if (filters.unread) params.unread = true;
      if (q) params.q = q;
      if (visibility === "hybrid" && scope === "mine") params.scope = "mine";
      const { data } = await api.get("/inbox/messages", { params });
      setMessages((prev) => [...prev, ...(data || []).filter((d) => !prev.some((p) => p.thread_id === d.thread_id))]);
      setHasMore((data || []).length >= PAGE);
    } finally { setLoadingMore(false); }
  };
  const loadList = async (keepLoaded = false) => {
    const params = { limit: keepLoaded ? Math.min(200, Math.max(PAGE, messages.length)) : PAGE, offset: 0 };
    if (filters.channel) params.channel = filters.channel;
    if (filters.category) params.category = filters.category;
    if (filters.unread) params.unread = true;
    if (q) params.q = q;
    if (visibility === "hybrid" && scope === "mine") params.scope = "mine";
    const { data } = await api.get("/inbox/messages", { params });
    setMessages(data || []);
    setHasMore((data || []).length >= params.limit);
    if (!selectedThreadId && data?.length && !isMobile) {
      setSelectedThreadId(data[0].thread_id);
    }
  };

  // Deep-link: open a specific thread when state.openThreadId or
  // ?thread={id} is provided. Used by Dashboard urgent feed + bell.
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const fromState = location.state?.openThreadId;
    const fromQuery = searchParams.get("thread");
    const target = fromState || fromQuery;
    if (!target || !messages.length) return;
    const match = messages.find((m) => m.thread_id === target || m.thread_id === decodeURIComponent(target));
    if (match) {
      setSelectedThreadId(match.thread_id);
      if (fromQuery) {
        const next = new URLSearchParams(searchParams);
        next.delete("thread");
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const loadStats = async () => {
    const params = {};
    if (visibility === "hybrid" && scope === "mine") params.scope = "mine";
    const { data } = await api.get("/inbox/stats", { params });
    setStats(data);
  };

  const loadThread = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      // IMAP Message-IDs commonly contain `<`, `>`, `@`, `?` — these break
      // raw path interpolation. Encode the id so any character survives.
      const { data } = await api.get(`/inbox/threads/${encodeURIComponent(id)}`);
      setThread(data);
      // Mark messages list locally as read
      setMessages((prev) => prev.map((m) => (m.thread_id === id ? { ...m, is_read: true } : m)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { api.get("/inbox/templates").then((r) => setTemplates(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    // Pick up the active workspace's visibility mode so we can render the right toggle
    cachedGet("/workspaces/mine", 5 * 60000).then((data) => {
      const stored = localStorage.getItem("pm_active_workspace_id");
      const found = (data || []).find((w) => w.id === stored);
      const primary = (data || []).find((w) => w.is_primary) || (data || [])[0];
      setVisibility(((found || primary)?.client_visibility) || "hybrid");
    }).catch(() => {});
  }, []);
  const effectiveScope = visibility === "hybrid" && scope === "mine" ? "mine" : "all";
  useEffect(() => { loadList(); loadStats(); /* eslint-disable-next-line */ }, [filters, q, effectiveScope]);

  // Keep the list fresh: silent refresh every 60s while visible + when the app returns to the foreground.
  const refreshRef = useRef(null);
  refreshRef.current = () => { if (document.visibilityState === "visible") { loadList(true).catch(() => {}); loadStats().catch(() => {}); } };
  useEffect(() => {
    const tick = () => refreshRef.current?.();
    const iv = setInterval(tick, 60000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, []);

  // Manual sync: force an IMAP pull on the server, then reload list + open thread.
  const [syncing, setSyncing] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);
  const [mailIssue, setMailIssue] = useState(null); // { email, error } when the IMAP connection fails
  const checkMailStatus = async () => {
    try {
      const { data } = await api.get("/comms/imap/status");
      const c = data?.connection;
      const broken = c?.last_sync_error && (!c.last_synced_at || (c.last_sync_error_at || "") > c.last_synced_at);
      setMailIssue(broken ? { email: c.email || c.username, error: c.last_sync_error } : null);
    } catch { /* ignore */ }
  };
  useEffect(() => { checkMailStatus(); }, []);
  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      let err = null;
      await api.post("/comms/imap/sync").catch((e) => { err = e?.response?.data?.detail || e.message; });
      await Promise.all([loadList(true), loadStats(), checkMailStatus()]);
      if (selectedThreadId) loadThread(selectedThreadId).catch(() => {});
      if (!err) setSyncedAt(new Date());
    } finally { setSyncing(false); }
  };
  useEffect(() => {
    loadThread(selectedThreadId);
    setSuggestions([]);
    setAiInsight(null);
    /* eslint-disable-next-line */
  }, [selectedThreadId]);
  // AI is on demand only (never blocks opening a thread). Results are cached per message server-side.
  const lastMsg = thread?.messages?.length ? thread.messages[thread.messages.length - 1] : null;
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const runAiAnalysis = async () => {
    if (!lastMsg?.body || aiInsightLoading) return;
    setAiInsightLoading(true);
    try {
      const r = await api.post("/ai/inbox/parse", { subject: thread.subject, body: lastMsg.body, sender: lastMsg.from_name || lastMsg.from_email, channel: lastMsg.channel, message_id: lastMsg.id });
      setAiInsight(r.data);
    } catch { setAiInsight(null); } finally { setAiInsightLoading(false); }
  };
  const runAiDraft = async () => {
    if (!lastMsg?.body || aiDraftLoading) return;
    setAiDraftLoading(true); setComposerKind("reply");
    try {
      const r = await api.post("/ai/inbox/reply-draft", { subject: thread.subject, body: lastMsg.body, message_id: lastMsg.id });
      setSuggestions(r.data.suggestions || []); setShowSuggestions(true);
    } catch {
      try { const r = await api.post("/inbox/ai-suggest", { body: lastMsg.body }); setSuggestions(r.data.suggestions || []); setShowSuggestions(true); } catch { setSuggestions([]); }
    } finally { setAiDraftLoading(false); }
  };

  const snooze = async (hours) => {
    if (!selectedThreadId) return;
    const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    await api.post(`/inbox/threads/${encodeURIComponent(selectedThreadId)}/snooze`, { until });
    await loadList();
  };

  const applyTemplate = (tpl) => {
    let body = tpl.body;
    if (selectedMeta?.client_name) body = body.replace(/{{client_name}}/g, selectedMeta.client_name);
    if (selectedMeta?.sender_entity) body = body.replace(/{{club_name}}/g, selectedMeta.sender_entity);
    body = body.replace(/{{agent_name}}/g, "Marco Rinaldi");
    setReply(body);
    setShowTemplates(false);
  };

  const [emailNotConfigured, setEmailNotConfigured] = useState(false);

  const sendReply = async () => {
    if (!reply.trim() || !selectedThreadId) return;
    if (composerKind === "reply") {
      try {
        await api.post(`/inbox/threads/${encodeURIComponent(selectedThreadId)}/reply`, { body: reply });
        setEmailNotConfigured(false);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 412 && detail?.code === "email_not_configured") {
          setEmailNotConfigured(true);
          return;
        }
        const msg = (typeof detail === "string" ? detail : detail?.message) || e?.message || "Reply failed";
        alert(`Couldn't send: ${msg}`);
        await loadThread(selectedThreadId);
        return;
      }
    } else {
      await api.post(`/inbox/threads/${encodeURIComponent(selectedThreadId)}/notes`, { body: reply });
    }
    setReply("");
    await loadThread(selectedThreadId);
    await loadStats();
  };

  const setCategory = async (cat) => {
    if (!selectedThreadId) return;
    await api.patch(`/inbox/threads/${encodeURIComponent(selectedThreadId)}`, { category: cat });
    await loadThread(selectedThreadId);
    await loadList();
    await loadStats();
  };

  const selectedMeta = useMemo(() => {
    if (!thread?.messages?.length) return null;
    return thread.messages[0];
  }, [thread]);

  return (
    <Layout title={isMobile ? "Inbox" : "Unified inbox"} subtitle="all channels in one place" compact
            right={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={syncNow}
                  disabled={syncing}
                  className="pm-btn-secondary h-10 w-10 lg:h-9 lg:w-auto lg:px-3 !rounded-full lg:!rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-60"
                  data-testid="inbox-sync-btn"
                  title={syncedAt ? `${t("Laatst gesynchroniseerd")} ${syncedAt.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })}` : t("Mail nu ophalen")}
                  aria-label={t("Vernieuwen")}
                >
                  <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /><span className="hidden lg:inline">{syncing ? t("Ophalen…") : t("Vernieuwen")}</span>
                </button>
                <Link
                  to="/contacts"
                  className="pm-btn-primary h-10 w-10 lg:h-9 lg:w-auto lg:px-3 !rounded-full lg:!rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
                  data-testid="inbox-start-conversation-btn"
                  title="Pick a contact and start a new conversation"
                  aria-label="Start conversation"
                >
                  <MessageSquarePlus size={16} className="lg:hidden" /><MessageSquarePlus size={12} className="hidden lg:block" /><span className="hidden lg:inline">Start conversation</span>
                </Link>
                <MockedBadge />
              </div>
            }>
      {mailIssue && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex flex-wrap items-center gap-2" data-testid="inbox-mail-issue-banner">
          <AlertTriangle size={15} className="text-amber-600 shrink-0" />
          <span className="font-semibold">{t("Je mailbox synchroniseert niet")}</span>
          <span className="text-amber-800">— {mailIssue.email}: {/AUTHENTICATIONFAILED|LOGIN rejected|AUTHENTICATE/i.test(mailIssue.error || "") ? t("wachtwoord geweigerd door je mailprovider. Nieuwe e-mails komen pas binnen nadat je opnieuw koppelt.") : (mailIssue.error || "").slice(0, 160)}</span>
          <Link to="/settings/integrations" className="ml-auto pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="inbox-mail-issue-fix">{t("Mailbox opnieuw koppelen")}</Link>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,340px)_minmax(0,1fr)] gap-4 lg:h-[calc(100vh-100px)] lg:min-h-[600px]">
        {/* Mobile: compact filter chips + counters */}
        <div className="lg:hidden space-y-2" data-testid="inbox-mobile-filters">
          {stats && (
            <div className="flex items-center gap-3 text-[11px] text-slate-500 px-1" data-testid="inbox-mobile-stats">
              <span>{stats.total} total</span><span className="text-emerald-700 font-semibold">{stats.unread} unread</span><span className="text-red-600 font-semibold">{stats.urgent} urgent</span>
            </div>
          )}
          <div className="flex gap-1.5 overflow-x-auto pm-scroll -mx-4 px-4 pb-1" style={{ scrollbarWidth: "none" }}>
            <button onClick={() => setFilters((f) => ({ ...f, unread: !f.unread }))} data-testid="m-filter-unread"
              className={`shrink-0 h-9 px-3 rounded-full text-xs font-bold border ${filters.unread ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Unread</button>
            <button onClick={() => setFilters((f) => ({ ...f, channel: null }))} data-testid="m-filter-channel-all"
              className={`shrink-0 h-9 px-3 rounded-full text-xs font-bold border ${!filters.channel ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>All</button>
            {CHANNELS.map((c) => { const Icon = CHANNEL_ICON[c]; const active = filters.channel === c; return (
              <button key={c} onClick={() => setFilters((f) => ({ ...f, channel: active ? null : c }))} data-testid={`m-filter-channel-${c}`}
                className={`shrink-0 h-9 px-3 rounded-full text-xs font-bold border flex items-center gap-1 ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}><Icon size={12} /> {CHANNEL_LABEL[c]}</button>); })}
            <select value={filters.category || ""} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value || null }))} data-testid="m-filter-category"
              className={`shrink-0 h-9 pl-3 pr-7 rounded-full text-xs font-bold border bg-white ${filters.category ? "border-slate-900 text-slate-900" : "border-slate-200 text-slate-700"}`}>
              <option value="">All categories</option>
              {CATEGORIES.map((cat) => <option key={cat} value={cat}>{CATEGORY_META[cat].label}</option>)}
            </select>
          </div>
        </div>
        {/* Left filter rail (desktop) */}
        <aside className="hidden lg:block pm-card p-3 overflow-y-auto pm-scroll" data-testid="inbox-filters">
          {/* Compact horizontal stats — total / unread / urgent on top of filters */}
          {stats && (
            <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="inbox-stats">
              <CompactStat label="Total" value={stats.total} />
              <CompactStat label="Unread" value={stats.unread} accent="emerald" />
              <CompactStat label="Urgent" value={stats.urgent} accent="red" />
            </div>
          )}

          <VisibilityToggle
            mode={visibility}
            scope={scope}
            onScopeChange={(s) => {
              setScope(s);
              try { localStorage.setItem("pm_inbox_scope", s); } catch (e) { /* noop */ }
              setSelectedThreadId(null);
              setThread(null);
            }}
          />

          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Channels</div>
          <button
            onClick={() => setFilters((f) => ({ ...f, channel: null }))}
            data-testid="filter-channel-all"
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-bold mb-1 ${
              !filters.channel ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            <span className="flex items-center gap-2"><InboxIcon size={14} /> All channels</span>
            <span className="font-mono-tab text-xs opacity-80">{stats?.total ?? ""}</span>
          </button>
          {CHANNELS.map((c) => {
            const Icon = CHANNEL_ICON[c];
            const active = filters.channel === c;
            return (
              <button
                key={c}
                onClick={() => setFilters((f) => ({ ...f, channel: active ? null : c }))}
                data-testid={`filter-channel-${c}`}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-bold mb-1 ${
                  active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <span className="flex items-center gap-2"><Icon size={14} /> {CHANNEL_LABEL[c]}</span>
                <span className="font-mono-tab text-xs opacity-80">{stats?.by_channel?.[c] ?? ""}</span>
              </button>
            );
          })}

          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mt-5 mb-2">Categories</div>
          <button
            onClick={() => setFilters((f) => ({ ...f, category: null }))}
            data-testid="filter-category-all"
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-bold mb-1 ${
              !filters.category ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            All categories
          </button>
          {CATEGORIES.map((cat) => {
            const meta = CATEGORY_META[cat];
            const active = filters.category === cat;
            return (
              <button
                key={cat}
                onClick={() => setFilters((f) => ({ ...f, category: active ? null : cat }))}
                data-testid={`filter-category-${cat}`}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-bold mb-1 ${
                  active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${meta.tone.replace("text-", "bg-").split(" ").find(c => c.startsWith("bg-"))}`} />
                  {meta.label}
                </span>
                <span className="font-mono-tab text-xs opacity-80">{stats?.by_category?.[cat] ?? ""}</span>
              </button>
            );
          })}

          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mt-5 mb-2">Quick filters</div>
          <button
            onClick={() => setFilters((f) => ({ ...f, unread: !f.unread }))}
            data-testid="filter-unread"
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-bold mb-1 ${
              filters.unread ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
            }`}
          >
            Unread only
          </button>
        </aside>

        {/* Middle message list */}
        <div className="pm-card overflow-hidden flex flex-col" data-testid="inbox-message-list">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10 lg:static">
            <Search size={14} className="text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search inbox…"
              className="bg-transparent outline-none text-sm flex-1"
              data-testid="inbox-search-input"
            />
            <span className="text-xs text-slate-400 font-mono-tab">{messages.length}</span>
          </div>
          <div className="flex-1 lg:overflow-y-auto pm-scroll" ref={threadRef}>
            {messages.length === 0 && (
              <div className="text-sm text-slate-500 p-6 text-center">No messages match these filters.</div>
            )}
            {messages.map((m) => (
              m.locked ? (
                <LockedMessageRow key={m.thread_id} m={m} onRefresh={loadList} />
              ) : (
                <MessageRow
                  key={m.thread_id}
                  m={m}
                  selected={selectedThreadId === m.thread_id}
                  onClick={() => setSelectedThreadId(m.thread_id)}
                />
              )
            ))}
            {hasMore && (
              <div ref={(el) => { if (!el) return; if (loadMoreObs.current) loadMoreObs.current.disconnect(); loadMoreObs.current = new IntersectionObserver((es) => { if (es[0].isIntersecting) loadMore(); }, { rootMargin: "200px" }); loadMoreObs.current.observe(el); }} className="p-3 flex justify-center" data-testid="inbox-load-more">
                <button onClick={loadMore} disabled={loadingMore} className="pm-btn-secondary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-2" data-testid="inbox-load-more-btn">{loadingMore ? <Loader2 size={12} className="animate-spin" /> : null} {t("Meer laden")}</button>
              </div>
            )}
          </div>
        </div>

        {/* Right detail pane — message-centric */}
{(isMobile && selectedThreadId ? (n) => createPortal(n, document.body) : (n) => n)(
        <div className={`pm-card overflow-hidden flex-col min-w-0 ${isMobile ? (selectedThreadId ? "flex fixed inset-0 z-[60] w-screen max-w-[100vw] overflow-x-hidden rounded-none border-0 bg-white" : "hidden") : "flex"}`} data-testid="inbox-detail">
          {isMobile && selectedThreadId && (
            <div className="flex items-center gap-2 px-2 h-12 border-b border-slate-200 shrink-0 min-w-0 w-full" style={{ paddingTop: "var(--sat)" }} data-testid="inbox-mobile-thread-bar">
              <button type="button" onClick={() => { setSelectedThreadId(null); setThread(null); }} className="w-11 h-11 rounded-full flex items-center justify-center text-slate-700 active:bg-slate-100" data-testid="inbox-mobile-back" aria-label="Terug naar inbox"><ChevronLeft size={22} /></button>
              <div className="text-sm font-bold text-slate-900 truncate flex-1">{selectedMeta?.sender_entity || selectedMeta?.sender_name || "Gesprek"}</div>
              <button type="button" onClick={() => setMoreOpen((v) => !v)} className="w-11 h-11 rounded-full flex items-center justify-center text-slate-700 active:bg-slate-100" data-testid="inbox-mobile-more" aria-label="Meer acties"><MoreHorizontal size={20} /></button>
            </div>
          )}
          {isMobile && moreOpen && selectedMeta && (
            <div className="absolute right-2 top-14 z-[70] w-56 rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 p-1.5 text-sm" data-testid="inbox-mobile-more-menu">
              <button onClick={() => { setComposerKind(composerKind === "note" ? "reply" : "note"); setMoreOpen(false); }} className="w-full text-left px-3 h-11 rounded-xl hover:bg-slate-50 flex items-center gap-2" data-testid="m-action-note"><Lock size={14} /> {composerKind === "note" ? "Reply instead of note" : "Internal note"}</button>
              <button onClick={() => { setShowTemplates((v) => !v); setMoreOpen(false); }} className="w-full text-left px-3 h-11 rounded-xl hover:bg-slate-50" data-testid="m-action-templates">Templates</button>
              <button onClick={() => { setShowReportPicker(true); setMoreOpen(false); }} className="w-full text-left px-3 h-11 rounded-xl hover:bg-slate-50 flex items-center gap-2" data-testid="m-action-attach"><Paperclip size={14} /> Attach report</button>
              <button onClick={() => { runAiAnalysis(); setMoreOpen(false); }} className="w-full text-left px-3 h-11 rounded-xl hover:bg-slate-50 flex items-center gap-2" data-testid="m-action-ai-analyse"><Sparkles size={14} /> {t("AI-analyse")}</button>
              <button onClick={() => { runAiDraft(); setMoreOpen(false); }} className="w-full text-left px-3 h-11 rounded-xl hover:bg-slate-50 flex items-center gap-2" data-testid="m-action-ai-draft"><Sparkles size={14} /> {t("Concept-antwoord")}</button>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">Snooze</div>
              <div className="flex gap-1 px-2 pb-1">{[1, 4, 24].map((h) => <button key={h} onClick={() => { snooze(h); setMoreOpen(false); }} className="flex-1 h-10 rounded-xl border border-slate-200 text-xs font-bold" data-testid={`m-snooze-${h}h`}>{h}h</button>)}</div>
            </div>
          )}
          {!thread || !selectedMeta ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500 text-sm gap-2 p-10">
              <InboxIcon size={32} className="text-slate-300" />
              <span>{loading ? "Loading thread…" : "Select a message to view the thread."}</span>
            </div>
          ) : (
            <>
              <ThreadHeader m={selectedMeta} onRecategorise={setCategory} />
              {/* Messages — the focus of the screen */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden pm-scroll px-4 sm:px-6 py-5 space-y-3 bg-slate-50/40 min-w-0">
                {groupMessagesByDay(thread.messages).map((group) => (
                  <div key={group.key}>
                    <DateDivider label={group.label} />
                    <div className="space-y-3 mt-3">
                      {group.messages.map((msg) => (
                        <MessageBubble key={msg.id} msg={msg} />
                      ))}
                    </div>
                  </div>
                ))}
                {thread.notes?.length > 0 && (
                  <div className="pt-4">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                      <Lock size={10} /> Internal notes
                    </div>
                    {thread.notes.map((n) => (
                      <div key={n.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-2"
                           data-testid={`inbox-note-${n.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 text-xs text-amber-800 font-bold">
                            <Lock size={11} /> {n.author}
                          </div>
                          <div className="text-xs text-slate-400 font-mono-tab">{fmtDateTime(n.created_at)}</div>
                        </div>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.body}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <AIInsightPanel insight={aiInsight} loading={aiInsightLoading} />
              <div className="border-t border-slate-200 px-4 py-2.5 shrink-0 bg-white" style={isMobile ? { paddingBottom: "calc(0.625rem + var(--sab))" } : undefined}>
                <div className="hidden lg:flex items-center gap-1.5 mb-2 flex-wrap">
                  <button
                    onClick={() => setComposerKind("reply")}
                    data-testid="composer-kind-reply"
                    className={`px-2.5 h-7 rounded-md text-[11px] font-bold ${
                      composerKind === "reply" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Reply · {CHANNEL_LABEL[selectedMeta.channel]}
                  </button>
                  <button
                    onClick={() => setComposerKind("note")}
                    data-testid="composer-kind-note"
                    className={`px-2.5 h-7 rounded-md text-[11px] font-bold flex items-center gap-1 ${
                      composerKind === "note" ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Lock size={10} /> Note
                  </button>
                  <button onClick={() => setShowTemplates((v) => !v)} className="px-2.5 h-7 rounded-md text-[11px] font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                          data-testid="composer-templates-btn">Templates</button>
                  <button onClick={() => setShowReportPicker(true)} className="px-2.5 h-7 rounded-md text-[11px] font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                          data-testid="composer-attach-report-btn"
                          title="Attach a player report PDF to this message">
                    <Paperclip size={10} /> Attach report
                  </button>
                  <button onClick={runAiAnalysis} disabled={aiInsightLoading || !lastMsg?.body} className="px-2.5 h-7 rounded-md text-[11px] font-bold bg-violet-50 border border-violet-200 text-violet-800 hover:bg-violet-100 flex items-center gap-1 disabled:opacity-50"
                          data-testid="composer-ai-analyse-btn">
                    {aiInsightLoading ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} {t("AI-analyse")}
                  </button>
                  <button onClick={suggestions.length > 0 ? () => { setShowSuggestions((v) => !v); setComposerKind("reply"); } : runAiDraft} disabled={aiDraftLoading || !lastMsg?.body} className="px-2.5 h-7 rounded-md text-[11px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100 flex items-center gap-1 disabled:opacity-50"
                          data-testid="composer-ai-suggest-btn">
                    {aiDraftLoading ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} {t("Concept-antwoord")}{suggestions.length > 0 ? ` (${suggestions.length})` : ""}
                  </button>
                  <div className="ml-auto flex items-center gap-1">
                    <span className="text-[9px] uppercase tracking-widest font-bold text-slate-400">Snooze</span>
                    {[1, 4, 24].map((h) => (
                      <button key={h} onClick={() => snooze(h)} className="px-1.5 h-6 rounded text-[10px] font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                              data-testid={`snooze-${h}h`}>{h}h</button>
                    ))}
                  </div>
                </div>
                {showTemplates && (
                  <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-2 max-h-32 overflow-y-auto pm-scroll" data-testid="templates-panel">
                    {templates.map((t) => (
                      <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`template-${t.id}`}
                              className="w-full text-left px-3 py-1.5 rounded hover:bg-white text-xs">
                        <div className="font-bold text-slate-900">{t.title}</div>
                        <div className="text-slate-500 truncate">{t.body.replace(/\n/g, " ")}</div>
                      </button>
                    ))}
                  </div>
                )}
                {showSuggestions && suggestions.length > 0 && composerKind === "reply" && (
                  <div className="mb-2 flex items-center gap-1.5 flex-wrap" data-testid="ai-suggestions">
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => { setReply(s); setShowSuggestions(false); }} data-testid={`ai-suggest-${i}`}
                              className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100 max-w-[280px] truncate">
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {isMobile && composerKind === "note" && <div className="mb-1.5 text-[11px] font-bold text-amber-700 flex items-center gap-1" data-testid="m-note-mode"><Lock size={10} /> Internal note — not sent externally</div>}
                <div className="flex gap-2 items-end">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={composerKind === "reply"
                      ? `Reply to ${selectedMeta.sender_name}…`
                      : "Internal note (not sent externally)"}
                    rows={reply.split("\n").length > 1 ? Math.min(reply.split("\n").length, 4) : 1}
                    className="pm-input flex-1 resize-none text-sm"
                    style={{ minHeight: 38 }}
                    data-testid="inbox-composer-input"
                  />
                  <button
                    onClick={sendReply}
                    disabled={!reply.trim()}
                    className="pm-btn-primary h-10 px-3 rounded-xl flex items-center gap-1.5 text-sm shrink-0 disabled:opacity-40"
                    data-testid="inbox-composer-send"
                    title={composerKind === "reply" ? "Send reply" : "Save note"}
                  >
                    <Send size={13} />
                  </button>
                </div>
                {emailNotConfigured && composerKind === "reply" && selectedMeta?.channel === "email" && (
                  <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="reply-email-not-configured">
                    <div className="flex items-start gap-2">
                      <span className="text-amber-700 mt-0.5">⚠</span>
                      <div>
                        <div className="text-[13px] font-bold text-amber-900">No email account connected</div>
                        <div className="text-[12px] text-amber-800 mt-1">Connect your Gmail or Outlook to send emails directly from your own address.</div>
                        <div className="flex items-center gap-2 mt-3">
                          <a href="/settings/integrations?connect=gmail" className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold" data-testid="reply-connect-gmail">Connect Gmail</a>
                          <a href="/settings/integrations?connect=outlook" className="pm-btn-secondary h-8 px-3 rounded-lg text-[11px] font-bold" data-testid="reply-connect-outlook">Connect Outlook</a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        )}
      </div>
      {showReportPicker && selectedMeta && (
        <ReportPickerModal
          mode="inbox"
          clientId={selectedMeta?.client_id}
          threadId={selectedThreadId}
          defaultRecipient={selectedMeta?.sender_email || ""}
          defaultChannel={selectedMeta?.channel === "whatsapp" ? "whatsapp" : selectedMeta?.channel === "sms" ? "sms" : "email"}
          defaultSubject={`${selectedMeta?.subject || ""}`}
          onClose={() => setShowReportPicker(false)}
          onSuccess={() => { setShowReportPicker(false); loadThread(selectedThreadId); }}
        />
      )}
    </Layout>
  );
}

/* ----------------- pieces ----------------- */

function StatChip({ label, value, sub, accent, icon: Icon }) {
  const tone =
    accent === "emerald" ? "text-emerald-700"
    : accent === "red" ? "text-red-600"
    : "text-slate-900";
  return (
    <div className="pm-card px-4 py-3" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className={`font-display font-black text-2xl font-mono-tab leading-tight ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function MessageRow({ m, selected, onClick }) {
  const meta = CATEGORY_META[m.category] || CATEGORY_META.unknown;
  const Icon = CHANNEL_ICON[m.channel] || Mail;
  return (
    <button
      onClick={onClick}
      data-testid={`inbox-row-${m.thread_id}`}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 flex gap-3 transition-colors duration-150 ${
        selected ? "bg-emerald-50/50 border-l-2 border-l-emerald-500" : "hover:bg-slate-50 border-l-2 border-l-transparent"
      }`}
    >
      <div className="relative shrink-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-[11px] ${meta.tone}`}>
          {initials(m.sender_name)}
        </div>
        {!m.is_read && (
          <span className="absolute -left-1 top-4 w-2 h-2 rounded-full" style={{ background: "var(--pm-green)" }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`text-sm truncate ${m.is_read ? "font-semibold text-slate-800" : "font-bold text-slate-900"}`}>
            {m.sender_name}{m.sender_entity ? <span className="text-slate-500 font-medium"> — {m.sender_entity}</span> : null}
          </div>
          <div className="ml-auto text-[11px] text-slate-400 font-mono-tab whitespace-nowrap">{relativeTime(m.created_at)}</div>
        </div>
        <div className={`text-sm truncate mt-0.5 ${m.is_read ? "text-slate-600" : "text-slate-800 font-medium"}`}>
          {m.subject || m.preview || m.body}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          <span className={`pm-pill ${meta.tone}`}>{meta.label}</span>
          <span className="pm-pill pm-pill-slate flex items-center gap-1"><Icon size={10} /> {CHANNEL_LABEL[m.channel]}</span>
          {m.priority === "urgent" && <span className="pm-pill pm-pill-red">Urgent</span>}
          {m.routing_reason === "unassigned_client" && (
            <span className="pm-pill pm-pill-amber" data-testid={`routing-unassigned-${m.thread_id}`}>Unassigned client</span>
          )}
          {m.routing_reason === "unknown_sender" && (
            <span className="pm-pill pm-pill-slate" data-testid={`routing-unknown-${m.thread_id}`}>Unknown sender</span>
          )}
          {m.is_demo && <span className="pm-pill pm-pill-amber text-[10px]" data-testid={`inbox-demo-${m.thread_id}`}>Demo data</span>}
          {m.client_name && <span className="text-[11px] text-slate-500 truncate">· {m.client_name}</span>}
        </div>
      </div>
    </button>
  );
}

function LockedMessageRow({ m, onRefresh }) {
  const Icon = CHANNEL_ICON[m.channel] || Mail;
  const [busy, setBusy] = useState(false);
  const pending = m.access_request_status === "pending";

  const requestAccess = async (e) => {
    e.stopPropagation();
    if (pending || busy) return;
    setBusy(true);
    try {
      await api.post(`/inbox/threads/${encodeURIComponent(m.thread_id)}/request-access`);
      onRefresh?.();
    } catch (err) {
      console.warn("Request access failed", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`inbox-locked-row-${m.thread_id}`}
      className="w-full text-left px-4 py-3 border-b border-slate-100 flex gap-3 bg-slate-50/40 border-l-2 border-l-amber-300"
    >
      <div className="shrink-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 text-slate-600">
          <Lock size={14} />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-slate-700 truncate flex items-center gap-1.5">
            <Lock size={11} className="text-amber-600" />
            Locked preview
          </div>
          <div className="ml-auto text-[11px] text-slate-400 font-mono-tab whitespace-nowrap">{relativeTime(m.created_at)}</div>
        </div>
        <div className="text-sm text-slate-700 truncate mt-0.5 font-medium">
          {m.sender_name}
          {m.sender_entity ? <span className="text-slate-500"> — {m.sender_entity}</span> : null}
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">
          {m.match_reason === "name_mention" ? (
            <>Mentions <span className="font-semibold text-slate-700">{m.client_name || "your client"}</span>{" "}· routed to {m.routed_to_agent_name || "another agent"}</>
          ) : (
            <>About <span className="font-semibold text-slate-700">{m.client_name || "your client"}</span>{" "}· routed to {m.routed_to_agent_name || "another agent"}</>
          )}
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className="pm-pill pm-pill-slate flex items-center gap-1"><Icon size={10} /> {CHANNEL_LABEL[m.channel]}</span>
          {pending ? (
            <span className="pm-pill pm-pill-amber text-[10px]" data-testid={`access-pending-${m.thread_id}`}>Access requested</span>
          ) : (
            <button
              onClick={requestAccess}
              disabled={busy}
              className="pm-pill pm-pill-green text-[10px] hover:opacity-80 disabled:opacity-50"
              data-testid={`request-access-${m.thread_id}`}
            >
              {busy ? "Requesting…" : "Request access"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadHeader({ m, onRecategorise }) {
  const meta = CATEGORY_META[m.category] || CATEGORY_META.unknown;
  const Icon = CHANNEL_ICON[m.channel] || Mail;
  const [open, setOpen] = useState(false);
  return (
    <div className="px-4 sm:px-5 py-3 border-b border-slate-200 flex items-center gap-3 min-w-0 w-full overflow-hidden">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-[11px] ${meta.tone} shrink-0`}>
        {initials(m.sender_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-base tracking-tight truncate">
          {m.sender_entity || m.sender_name}
          {m.sender_entity && <span className="text-slate-500 font-normal text-sm"> — {m.sender_name}</span>}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap text-[11px]">
          <span className={`pm-pill ${meta.tone}`}>{meta.label}</span>
          <span className="pm-pill pm-pill-slate flex items-center gap-1"><Icon size={10} /> {CHANNEL_LABEL[m.channel]}</span>
          {m.client_id && m.client_name && (
            <Link to={`/clients/${m.client_id}`} className="font-bold text-emerald-700 hover:text-emerald-800 underline underline-offset-2" data-testid="inbox-link-to-client">
              → {m.client_name}
            </Link>
          )}
          {m.to_address && <span className="text-slate-400 font-mono-tab truncate max-w-full">to {m.to_address}</span>}
        </div>
      </div>
      <div className="relative shrink-0">
        <button
          onClick={() => setOpen((v) => !v)}
          className="pm-btn-secondary h-8 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1"
          data-testid="thread-retag-btn"
        >
          Retag <ChevronDown size={12} />
        </button>
        {open && (
          <div className="absolute right-0 mt-2 z-20 pm-card p-2 w-44 shadow-lg" data-testid="retag-menu">
            {CATEGORIES.map((c) => {
              const cm = CATEGORY_META[c];
              return (
                <button
                  key={c}
                  onClick={() => { onRecategorise(c); setOpen(false); }}
                  data-testid={`retag-${c}`}
                  className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 flex items-center gap-2"
                >
                  <span className={`pm-pill ${cm.tone}`}>{cm.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Wide, fixed-width HTML newsletters get scaled down to the bubble width
// instead of being clipped (mobile).
function useFitEmailWidth(ref, deps) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => {
      el.style.transform = ""; el.style.height = ""; el.style.width = "";
      const avail = el.parentElement?.clientWidth || el.clientWidth;
      const need = el.scrollWidth;
      if (need > avail + 2 && avail > 0) {
        const r = avail / need;
        el.style.width = `${need}px`;
        el.style.transformOrigin = "top left";
        el.style.transform = `scale(${r})`;
        el.style.height = `${el.scrollHeight * r}px`;
      }
    };
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(el.parentElement || el);
    el.querySelectorAll("img").forEach((img) => img.addEventListener("load", fit));
    return () => ro?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function MessageBubble({ msg }) {
  const htmlRef = useRef(null);
  useFitEmailWidth(htmlRef, [msg.id, msg.body_html]);
  const outbound = msg.direction === "outbound";
  const ChannelIcon = CHANNEL_ICON[msg.channel] || Mail;
  const channelTone = {
    whatsapp: "text-emerald-700 bg-emerald-100",
    sms:      "text-sky-700 bg-sky-100",
    email:    "text-slate-700 bg-slate-100",
    outlook:  "text-blue-700 bg-blue-100",
    gmail:    "text-rose-700 bg-rose-100",
    call:     "text-violet-700 bg-violet-100",
  }[msg.channel] || "text-slate-700 bg-slate-100";
  return (
    <div className={`flex items-end gap-2 ${outbound ? "justify-end" : "justify-start"}`} data-testid={`inbox-bubble-${msg.id}`}>
      {!outbound && (
        <div className={`w-7 h-7 rounded-lg ${channelTone} flex items-center justify-center shrink-0 mb-4`}>
          <ChannelIcon size={12} />
        </div>
      )}
      <div className={`max-w-[92%] sm:max-w-[78%] min-w-0 overflow-hidden rounded-2xl px-4 py-3 border shadow-sm ${
        outbound
          ? "bg-emerald-500 text-white border-emerald-500 rounded-br-sm"
          : "bg-white text-slate-900 border-slate-200 rounded-bl-sm"
      }`}>
        {!outbound && (
          <div className="text-[11px] font-bold mb-1 text-slate-700">
            {msg.sender_name}{msg.sender_entity ? ` · ${msg.sender_entity}` : ""}
          </div>
        )}
        {msg.subject && msg.channel === "email" && (
          <div className={`text-sm font-bold mb-1 ${outbound ? "text-white" : "text-slate-900"}`}>{msg.subject}</div>
        )}
        {(() => {
          // Pick the richest source we have, then strip CSS-only artifacts
          // (legacy IMAP rows sometimes stored just the contents of a <style>
          // block as plain text). If real HTML tags survive the cleanup, we
          // sanitize + render as HTML; otherwise we fall back to plain text.
          const source = msg.body_html || msg.body || "";
          const cleaned = stripCssArtifacts(source);
          const renderAsHtml = !outbound && /<\s*[a-z][\w-]*\b/i.test(cleaned);
          if (renderAsHtml) {
            return (
              <div
                ref={htmlRef}
                className="pm-email-body text-[14px] leading-[1.55] text-slate-900 overflow-hidden"
                data-testid={`message-body-html-${msg.id}`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(cleaned, {
                    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input", "button"],
                    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
                    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|data:image\/)/i,
                  }),
                }}
              />
            );
          }
          // Plain-text fallback — also runs cleaned through so any leftover
          // CSS / template placeholders don't show up as visible text.
          const plain = cleaned || msg.body || "";
          return (
            <div className={`text-[14px] whitespace-pre-wrap leading-[1.55] ${outbound ? "text-white" : "text-slate-900"}`} data-testid={`message-body-${msg.id}`}>
              {plain}
            </div>
          );
        })()}
        {(msg.attachments || []).length > 0 && (
          <AttachmentChips message={msg} outbound={outbound} />
        )}
        {msg.duration_sec && (
          <div className={`text-[11px] mt-2 font-mono-tab ${outbound ? "text-white/80" : "text-slate-500"}`}>
            <Phone size={10} className="inline mr-1" /> Call · {Math.floor(msg.duration_sec / 60)}m {msg.duration_sec % 60}s
          </div>
        )}
        <div className={`text-[10px] mt-1.5 font-mono-tab text-right flex items-center justify-end gap-1 ${outbound ? "text-white/70" : "text-slate-400"}`}>
          {outbound && msg.delivery_status && (
            <span data-testid={`delivery-${msg.id}`}
                  className={`px-1.5 py-0.5 rounded ${
                    msg.delivery_status === "failed" ? "bg-red-200 text-red-800"
                    : msg.delivery_status === "delivered" || msg.delivery_status === "read" ? "bg-emerald-700 text-white"
                    : msg.delivery_status === "queued" || msg.delivery_status === "sent" ? "bg-emerald-600 text-white"
                    : msg.delivery_status === "logged_only" ? "bg-amber-300 text-amber-900"
                    : ""
                  }`}
                  title={msg.delivery_error || msg.delivery_status}>
              {msg.delivery_status === "logged_only" ? "saved" : msg.delivery_status}
            </span>
          )}
          <span>{fmtTime(msg.created_at)}</span>
        </div>
      </div>
      {outbound && (
        <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mb-4">
          <ChannelIcon size={12} />
        </div>
      )}
    </div>
  );
}

function DateDivider({ label }) {
  return (
    <div className="flex items-center gap-3 my-3" data-testid={`date-divider-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex-1 h-px bg-slate-200" />
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 bg-slate-50/40 px-2">
        {label}
      </span>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function CompactStat({ label, value, accent }) {
  const tone =
    accent === "emerald" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : accent === "red" ? "text-red-700 bg-red-50 border-red-200"
    : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={`px-2 py-1.5 rounded-lg border ${tone}`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</div>
      <div className="font-display font-black text-base font-mono-tab leading-tight">{value}</div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

function groupMessagesByDay(messages) {
  const groups = [];
  let current = null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (const m of messages || []) {
    const d = new Date(m.created_at);
    const dayKey = d.toISOString().slice(0, 10);
    if (!current || current.key !== dayKey) {
      const dayOnly = new Date(d); dayOnly.setHours(0, 0, 0, 0);
      const diff = Math.round((today - dayOnly) / 86400000);
      const label = diff === 0 ? "Today" : diff === 1 ? "Yesterday"
        : diff < 7 ? d.toLocaleDateString("en-GB", { weekday: "long" })
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
      current = { key: dayKey, label, messages: [] };
      groups.push(current);
    }
    current.messages.push(m);
  }
  return groups;
}

function MockedBadge() {
  // Twilio SMS + WhatsApp Business are live.
  // Gmail/Outlook are real OAuth. Surface a clean live-status pill rather than a misleading "mocked everything" warning.
  return (
    <div className="hidden md:flex items-center gap-1.5 px-3 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold" data-testid="inbox-live-status">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
      Live · SMS / WhatsApp / Email
    </div>
  );
}

const URGENCY_TONE = {
  high: "bg-red-50 border-red-200 text-red-800",
  medium: "bg-amber-50 border-amber-200 text-amber-800",
  low: "bg-slate-50 border-slate-200 text-slate-700",
};

const CASE_TYPE_LABEL = {
  transfer_offer: "Transfer offer",
  contract: "Contract",
  medical: "Medical",
  legal: "Legal",
  finance: "Finance",
  scouting: "Scouting",
  general: "General",
};

function AIInsightPanel({ insight, loading }) {
  const [open, setOpen] = useState(false);
  if (loading && !insight) {
    return (
      <div className="border-t border-slate-200 px-6 py-2 bg-gradient-to-r from-violet-50/40 to-emerald-50/40" data-testid="ai-insight-loading">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-violet-700">
          <Sparkles size={12} className="animate-pulse" /> Claude is reading the thread…
        </div>
      </div>
    );
  }
  if (!insight) return null;
  const tone = URGENCY_TONE[insight.urgency] || URGENCY_TONE.low;
  return (
    <div className="border-t border-slate-200 bg-gradient-to-r from-violet-50/40 to-emerald-50/40" data-testid="ai-insight-panel">
      {/* Always-visible 1-line summary bar — clickable to expand for details */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-6 py-2 flex items-center gap-2 text-left hover:bg-violet-50/60 transition-colors"
        data-testid="ai-insight-toggle"
        aria-expanded={open}
      >
        <Sparkles size={12} className="text-violet-700 shrink-0" />
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700 shrink-0">
          {insight.stub ? "Rules" : "Claude"}
        </span>
        <span className={`pm-pill text-[10px] ${tone}`} data-testid="ai-insight-urgency">
          {insight.urgency?.toUpperCase()}
        </span>
        <span className="pm-pill pm-pill-slate text-[10px]" data-testid="ai-insight-case-type">
          {CASE_TYPE_LABEL[insight.case_type] || insight.case_type}
        </span>
        {insight.needs_human_review && (
          <span className="pm-pill pm-pill-amber text-[10px] flex items-center gap-1" data-testid="ai-insight-review">
            <Zap size={10} /> Review
          </span>
        )}
        {!open && insight.summary && (
          <span className="text-xs text-slate-600 truncate flex-1 ml-1 hidden md:block">
            {insight.summary}
          </span>
        )}
        <ChevronDown size={12} className={`ml-auto text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-3 space-y-2">
          {insight.summary && (
            <div className="text-xs text-slate-700 leading-relaxed" data-testid="ai-insight-summary">
              <span className="font-bold">Summary: </span>{insight.summary}
            </div>
          )}
          {Array.isArray(insight.suggested_actions) && insight.suggested_actions.length > 0 && (
            <ul className="text-xs text-slate-700 list-disc pl-5 space-y-0.5" data-testid="ai-insight-actions">
              {insight.suggested_actions.slice(0, 3).map((a, i) => (
                <li key={i} data-testid={`ai-insight-action-${i}`}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


function VisibilityToggle({ mode, scope, onScopeChange }) {
  if (mode === "shared") {
    // Nothing to toggle — every member sees everything
    return null;
  }
  if (mode === "private") {
    return (
      <div className="mb-3 px-2.5 py-2 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-1.5 text-[11px] text-slate-700" data-testid="inbox-visibility-badge-private">
        <span className="w-2 h-2 rounded-full bg-amber-500" />
        <span className="font-bold">Private inbox</span>
        <span className="text-slate-500">— you only see threads for clients assigned to you.</span>
      </div>
    );
  }
  // hybrid → show Mine / All toggle
  return (
    <div className="mb-3" data-testid="inbox-scope-toggle">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">View</div>
      <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-0.5">
        {[
          { id: "all",  label: "All threads" },
          { id: "mine", label: "Mine only" },
        ].map((opt) => (
          <button
            key={opt.id}
            onClick={() => onScopeChange(opt.id)}
            data-testid={`inbox-scope-${opt.id}`}
            className={`h-8 rounded-md text-[11px] font-bold transition-all ${
              scope === opt.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ──────────────── Email attachments — view + save to note / docs ──────────────── */

const MIME_LABEL = {
  "application/pdf": { icon: FileText, color: "text-red-600 bg-red-50 border-red-200", label: "PDF" },
  "image/png":       { icon: FileImage, color: "text-violet-600 bg-violet-50 border-violet-200", label: "PNG" },
  "image/jpeg":      { icon: FileImage, color: "text-violet-600 bg-violet-50 border-violet-200", label: "JPEG" },
  "image/jpg":       { icon: FileImage, color: "text-violet-600 bg-violet-50 border-violet-200", label: "JPG" },
  "image/heic":      { icon: FileImage, color: "text-violet-600 bg-violet-50 border-violet-200", label: "HEIC" },
  "image/webp":      { icon: FileImage, color: "text-violet-600 bg-violet-50 border-violet-200", label: "WEBP" },
};
function mimeMeta(mime) {
  if (MIME_LABEL[mime]) return MIME_LABEL[mime];
  if ((mime || "").startsWith("image/")) return MIME_LABEL["image/png"];
  return { icon: FileText, color: "text-slate-600 bg-slate-50 border-slate-200", label: (mime || "").split("/")[1]?.toUpperCase()?.slice(0, 6) || "FILE" };
}
function humanSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function AttachmentChips({ message, outbound }) {
  const [busyAtt, setBusyAtt] = useState(null);
  const [saved, setSaved] = useState({}); // { [att_id]: "note" | "docs" }
  const [toastMsg, setToastMsg] = useState("");
  const [smartFor, setSmartFor] = useState(null); // attachment being smart-attached

  const flash = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 2200);
  };

  const view = async (att) => {
    try {
      const token = localStorage.getItem("pm_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/inbox/messages/${encodeURIComponent(message.id)}/attachments/${att.id}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener");
    } catch (e) {
      flash("Could not open attachment");
    }
  };

  const saveToNote = async (att) => {
    setBusyAtt(att.id);
    try {
      const r = await api.post(`/inbox/messages/${encodeURIComponent(message.id)}/attachments/${att.id}/save-to-note`, {});
      setSaved((s) => ({ ...s, [att.id]: "note" }));
      flash(`Saved to a new note · /notes`);
      // eslint-disable-next-line no-console
      console.info("saved-to-note", r.data);
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not save to note");
    } finally {
      setBusyAtt(null);
    }
  };

  const saveToDocs = async (att, clientId = null) => {
    setBusyAtt(att.id);
    try {
      const r = await api.post(`/inbox/messages/${encodeURIComponent(message.id)}/attachments/${att.id}/save-to-document`, clientId ? { client_id: clientId } : {});
      setSaved((s) => ({ ...s, [att.id]: "docs" }));
      flash(clientId ? "Saved to scouting docs · linked to client" : "Saved to scouting docs");
      // eslint-disable-next-line no-console
      console.info("saved-to-document", r.data);
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not save to docs");
    } finally {
      setBusyAtt(null);
      setSmartFor(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200/30" data-testid={`message-attachments-${message.id}`}>
      <div className={`flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.22em] font-bold ${outbound ? "text-white/70" : "text-slate-500"}`}>
        <Paperclip size={11} /> Attachments · {message.attachments.length}
      </div>
      <div className="space-y-1.5">
        {message.attachments.map((att) => {
          const meta = mimeMeta(att.mime);
          const Icon = meta.icon;
          const isSaved = saved[att.id];
          const isBusy = busyAtt === att.id;
          return (
            <div
              key={att.id}
              className={`flex items-center gap-2 rounded-lg border bg-white/95 px-2.5 py-2 ${outbound ? "border-white/20" : "border-slate-200"}`}
              data-testid={`attachment-${att.id}`}
            >
              <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${meta.color}`}>
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate" title={att.filename}>{att.filename}</div>
                <div className="text-[11px] text-slate-500 font-mono-tab">{meta.label} · {humanSize(att.size)}</div>
              </div>
              <button
                onClick={() => view(att)}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                title="View"
                data-testid={`attachment-view-${att.id}`}
              >
                <Eye size={13} />
              </button>
              <button
                onClick={() => view(att)}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                title="Download"
                data-testid={`attachment-download-${att.id}`}
              >
                <Download size={13} />
              </button>
              <button
                onClick={() => saveToNote(att)}
                disabled={isBusy || isSaved}
                className={`h-8 px-2 rounded-lg flex items-center gap-1 text-[11px] font-bold transition-colors ${
                  isSaved === "note" ? "bg-emerald-100 text-emerald-800" :
                  "bg-slate-50 hover:bg-amber-50 text-slate-700 hover:text-amber-900 border border-slate-200 hover:border-amber-200"
                } disabled:opacity-50`}
                title={isSaved === "note" ? "Saved to notes" : "Save to notes"}
                data-testid={`attachment-save-note-${att.id}`}
              >
                {isSaved === "note" ? <><Check size={11} /> Saved</> : <><NotebookPen size={11} /> Note</>}
              </button>
              <button
                onClick={() => setSmartFor(att)}
                disabled={isBusy || isSaved}
                className={`h-8 px-2 rounded-lg flex items-center gap-1 text-[11px] font-bold transition-colors ${
                  isSaved === "docs" ? "bg-emerald-100 text-emerald-800" :
                  "bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-900 border border-slate-200 hover:border-indigo-200"
                } disabled:opacity-50`}
                title={isSaved === "docs" ? "Saved to scouting docs" : "Save to scouting docs"}
                data-testid={`attachment-save-doc-${att.id}`}
              >
                {isSaved === "docs" ? <><Check size={11} /> Saved</> : <><FolderInput size={11} /> Docs</>}
              </button>
            </div>
          );
        })}
      </div>
      {toastMsg && (
        <div className="mt-2 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid="attachment-flash">
          <Check size={11} /> {toastMsg}
        </div>
      )}
      {smartFor && (
        <SmartAttachModal
          messageId={message.id}
          attachment={smartFor}
          onClose={() => setSmartFor(null)}
          onConfirm={(clientId) => saveToDocs(smartFor, clientId)}
        />
      )}
    </div>
  );
}

function SmartAttachModal({ messageId, attachment, onClose, onConfirm }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [usedVision, setUsedVision] = useState(false);
  const [picked, setPicked] = useState(null);
  const [search, setSearch] = useState("");
  const [allClients, setAllClients] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.post(`/inbox/messages/${encodeURIComponent(messageId)}/attachments/${attachment.id}/suggest-client`, {})
      .then((r) => {
        if (cancelled) return;
        setSuggestions(r.data.suggestions || []);
        setUsedVision(!!r.data.used_vision);
        if (r.data.suggestions?.[0]?.confidence >= 0.7) setPicked(r.data.suggestions[0].id);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    api.get("/clients").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : r.data?.clients || [];
      setAllClients(arr.map((c) => ({ id: c.id, full_name: c.full_name, current_club: c.current_club })));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [messageId, attachment.id]);

  const filtered = !search ? [] : allClients.filter((c) =>
    (c.full_name || "").toLowerCase().includes(search.toLowerCase())
  ).slice(0, 6);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="smart-attach-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-600 flex items-center gap-1.5">
              <Sparkles size={11} /> Smart-attach to client
            </div>
            <div className="font-display text-base font-black tracking-tight truncate max-w-[400px]" title={attachment.filename}>{attachment.filename}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="smart-attach-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-center py-6 text-slate-500 text-sm flex items-center justify-center gap-2" data-testid="smart-attach-loading">
              <Loader2 size={14} className="animate-spin" /> Looking for the right client…
            </div>
          ) : (
            <>
              {suggestions.length > 0 ? (
                <>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1.5">
                    <Sparkles size={11} className="text-violet-600" />
                    Suggestions {usedVision && <span className="text-violet-600">· Claude vision</span>}
                  </div>
                  <div className="space-y-1.5" data-testid="smart-attach-suggestions">
                    {suggestions.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setPicked(s.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                          picked === s.id ? "border-violet-400 bg-violet-50 ring-2 ring-violet-200" : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`smart-attach-pick-${s.id}`}
                      >
                        {s.photo_url ? (
                          <img src={s.photo_url} alt={s.full_name} className="w-9 h-9 rounded-lg object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-700 text-sm">
                            {s.full_name?.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-slate-900 truncate">{s.full_name}</div>
                          <div className="text-[11px] text-slate-500 truncate">{s.position} · {s.current_club}</div>
                          <div className="text-[10px] text-violet-700 mt-0.5 truncate italic">{s.reason}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-[10px] font-bold uppercase tracking-wider ${s.confidence >= 0.85 ? "text-emerald-700" : s.confidence >= 0.6 ? "text-amber-700" : "text-slate-500"}`}>
                            {Math.round(s.confidence * 100)}%
                          </div>
                          {picked === s.id && <Check size={14} className="text-violet-600 ml-auto mt-1" />}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-xs text-slate-500 italic py-2">No automatic matches found. Search below or save without a client link.</div>
              )}

              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Or pick manually</div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search clients by name…"
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm"
                  data-testid="smart-attach-search"
                />
                {filtered.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-44 overflow-y-auto">
                    {filtered.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setPicked(c.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left ${picked === c.id ? "bg-violet-100 text-violet-900" : "hover:bg-slate-50"}`}
                        data-testid={`smart-attach-manual-${c.id}`}
                      >
                        <span className="truncate">{c.full_name}</span>
                        <span className="text-[11px] text-slate-500 truncate ml-2">{c.current_club}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 flex items-center gap-3 border-t border-slate-100">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" data-testid="smart-attach-cancel">Cancel</button>
          <div className="flex-1" />
          <button
            onClick={() => onConfirm(null)}
            className="h-10 px-3 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900"
            data-testid="smart-attach-skip"
            title="Save the file without linking to a client"
          >
            Skip — save without link
          </button>
          <button
            onClick={() => onConfirm(picked)}
            disabled={!picked}
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-40"
            data-testid="smart-attach-confirm"
          >
            <FolderInput size={12} /> Save to docs
          </button>
        </div>
      </div>
    </div>
  );
}
